import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import {
  GENRES,
  REGIONS,
  SORT_LABELS,
  YEARS,
  filterMovies,
  paginate,
  sortMovies,
  type SortKey,
} from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 10;

const CATEGORY_META: Record<string, { label: string; desc: string }> = {
  movie: {
    label: "电影",
    desc: "最新电影资源，支持按类型、地区、年份复合筛选，快速找到你想看的好片。",
  },
  tv: {
    label: "电视剧",
    desc: "热播剧集与经典剧作，按题材、地区、年份筛选，追剧更高效。",
  },
  anime: {
    label: "动漫",
    desc: "国产与海外动漫作品合集，涵盖热血、科幻、治愈等多种风格。",
  },
  variety: {
    label: "综艺",
    desc: "音乐、真人秀等热门综艺节目，轻松浏览最新上线的精彩内容。",
  },
};

const filterGroups: FilterGroup[] = [
  { key: "genre", label: "类型", options: GENRES.map((g) => ({ label: g, value: g })) },
  { key: "region", label: "地区", options: REGIONS.map((r) => ({ label: r, value: r })) },
  { key: "year", label: "年份", options: YEARS.map((y) => ({ label: `${y}年`, value: String(y) })) },
];

const sortOptions: SortKey[] = ["latest", "popular", "rating"];

export default function CategoryPage() {
  const { type = "movie" } = useParams();
  const meta = CATEGORY_META[type];

  const [values, setValues] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title: `${meta?.label ?? "影视分类"} - 91影视`,
    description: meta
      ? `${meta.desc}海量${meta.label}资源尽在91影视。`
      : "91影视影视分类列表，按多维度筛选影视作品。",
    keywords: `91影视,${meta?.label ?? "影视分类"},影视筛选,${meta?.label ?? "影视"}推荐`,
  });

  const result = useMemo(() => {
    const filtered = filterMovies({
      type,
      region: values.region || undefined,
      genre: values.genre || undefined,
      year: values.year ? Number(values.year) : undefined,
    });
    return paginate(sortMovies(filtered, sort), page, PAGE_SIZE);
  }, [type, values, sort, page]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setValues({});
    setPage(1);
  };

  const hasActiveFilter = Boolean(values.region || values.year || values.genre);

  if (!meta) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的分类页面不存在，返回首页继续浏览吧。"
          actionLabel="返回首页"
          to="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{meta.label}</span>
      </nav>

      <header className="mt-4">
        <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">{meta.label}频道</h1>
        <p className="mt-2 max-w-2xl text-sm text-foreground-500">{meta.desc}</p>
      </header>

      <div className="mt-6">
        <FilterPanel
          groups={filterGroups}
          values={values}
          onChange={handleChange}
          onReset={handleReset}
          hasActiveFilter={hasActiveFilter}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{result.total}</span> 部作品
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">排序：</span>
          {sortOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setSort(option);
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                sort === option
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {result.items.length > 0 ? (
          <MovieGrid movies={result.items} />
        ) : (
          <EmptyState
            icon="ri-movie-line"
            title="没有找到符合条件的影片"
            description="试着调整筛选条件，或重置后重新浏览。"
            actionLabel="重置筛选"
            onAction={handleReset}
          />
        )}
      </div>

      <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
    </div>
  );
}